import { useState, useEffect, useRef } from 'react';
import { useAuth } from '@clerk/clerk-react';
import { useInfiniteQuery } from '@tanstack/react-query';
import { api, STAGE_VALUES, type LineStage, type RfqListResponse } from '@/lib/api';
import { RfqRow } from '@/components/rfq-row';
import { RfqBoard } from '@/components/rfq-board';
import { FlagToggle } from '@/components/flag-toggle';
import { useToggleRfqFlag } from '@/lib/use-toggle-rfq-flag';
import { STAGE_LABEL } from '@/lib/format';
import { SOURCING_CHANNELS, SOURCING_CHANNEL_VALUES, type SourcingChannel } from '@shared/sourcing-channels';

type SourcingFilter = '' | 'unposted' | SourcingChannel;

interface Props {
  selectedRfqId: string | null;
}

export function RfqList({ selectedRfqId }: Props) {
  const { getToken } = useAuth();
  // 'all' = the chronological keyset list. 'board' = the manually ordered
  // flagged focus list. Two genuinely different queries, so a tab rather than
  // another checkbox: the existing filter row composes (AOG + urgent + …),
  // whereas board mode replaces the ordering wholesale and cannot paginate.
  const [tab, setTab] = useState<'all' | 'board'>('all');
  const [q, setQ] = useState('');
  const [stage, setStage] = useState<LineStage | ''>('');
  const [aogOnly, setAogOnly] = useState(false);
  const [urgentOnly, setUrgentOnly] = useState(false);
  const [flaggedOnly, setFlaggedOnly] = useState(false);
  const [quoteSentOnly, setQuoteSentOnly] = useState(false);
  const [sourcing, setSourcing] = useState<SourcingFilter>('');

  const params = new URLSearchParams();
  if (q.trim()) params.set('q', q.trim());
  if (stage) params.set('stage', stage);
  if (aogOnly) params.set('aog', 'true');
  if (urgentOnly) params.set('urgent', 'true');
  if (flaggedOnly) params.set('flagged', 'true');
  if (quoteSentOnly) params.set('quoteSent', 'true');
  if (sourcing === 'unposted') params.set('unpostedOnly', 'true');
  else if (sourcing) params.set('sourcingChannel', sourcing);
  const queryString = params.toString();

  // Infinite/keyset pagination — the API returns 50 RFQs + a nextCursor
  // (received_at). Without consuming the cursor the list could only ever show
  // the newest 50, which silently hid older RFQs once a busy day filled the
  // first page. "Load more" walks the cursor back through full history.
  const {
    data, isLoading, isError, error,
    fetchNextPage, hasNextPage, isFetchingNextPage,
  } = useInfiniteQuery({
    queryKey: ['rfqs', queryString],
    queryFn: ({ pageParam }) => {
      const p = new URLSearchParams(queryString);
      if (pageParam) p.set('cursor', pageParam as string);
      const qs = p.toString();
      return api<RfqListResponse>(`/api/rfqs${qs ? `?${qs}` : ''}`, {
        tokenGetter: () => getToken(),
      });
    },
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
    staleTime: 10_000,
  });

  const rfqs = data?.pages.flatMap((p) => p.rfqs) ?? [];

  // One shared mutation for the whole list. `variables.id` identifies which row
  // is mid-flight, so only that toggle dims rather than all of them.
  const toggleFlag = useToggleRfqFlag();

  // Infinite scroll: observe a sentinel at the bottom of the scroll container
  // and fetch the next page as it nears view (200px early so it feels seamless).
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const sentinelRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel || !hasNextPage) return;
    const obs = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting && hasNextPage && !isFetchingNextPage) {
          fetchNextPage();
        }
      },
      { root: scrollRef.current, rootMargin: '200px' },
    );
    obs.observe(sentinel);
    return () => obs.disconnect();
  }, [hasNextPage, isFetchingNextPage, fetchNextPage, rfqs.length]);

  return (
    <div className="flex flex-col h-full">
      <div className="px-4 pt-3 flex items-center gap-1" role="tablist" aria-label="RFQ views">
        {([['all', 'All RFQs'], ['board', '🚩 Priority']] as const).map(([key, label]) => (
          <button
            key={key}
            type="button"
            role="tab"
            aria-selected={tab === key}
            onClick={() => setTab(key)}
            className={
              'px-3 py-1.5 text-xs font-medium rounded-md transition-colors min-h-[36px] ' +
              (tab === key
                ? 'bg-muted text-foreground'
                : 'text-muted-foreground hover:text-foreground hover:bg-muted/50')
            }
          >
            {label}
          </button>
        ))}
      </div>

      {tab === 'board' ? (
        <RfqBoard selectedRfqId={selectedRfqId} />
      ) : (
      <>
      <div className="px-4 py-3 border-b border-border space-y-3">
        <div className="relative">
          <input
            type="search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search PN, RFQ #, PO #, quote #, invoice #…"
            className="w-full pl-9 pr-3 py-2 text-sm bg-background border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary/40"
          />
          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">⌕</span>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <select
            value={stage}
            onChange={(e) => setStage(e.target.value as LineStage | '')}
            className="px-2 py-1 text-xs bg-background border border-border rounded"
          >
            <option value="">All stages</option>
            {STAGE_VALUES.map((s) => (
              <option key={s} value={s}>{STAGE_LABEL[s]}</option>
            ))}
          </select>
          <label className="flex items-center gap-1.5 text-xs cursor-pointer">
            <input
              type="checkbox"
              checked={aogOnly}
              onChange={(e) => setAogOnly(e.target.checked)}
              className="rounded"
            />
            AOG only
          </label>
          <label className="flex items-center gap-1.5 text-xs cursor-pointer">
            <input
              type="checkbox"
              checked={urgentOnly}
              onChange={(e) => setUrgentOnly(e.target.checked)}
              className="rounded"
            />
            Urgent only
          </label>
          <label className="flex items-center gap-1.5 text-xs cursor-pointer">
            <input
              type="checkbox"
              checked={flaggedOnly}
              onChange={(e) => setFlaggedOnly(e.target.checked)}
              className="rounded"
            />
            🚩 Flagged
          </label>
          <label className="flex items-center gap-1.5 text-xs cursor-pointer">
            <input
              type="checkbox"
              checked={quoteSentOnly}
              onChange={(e) => setQuoteSentOnly(e.target.checked)}
              className="rounded"
            />
            ✅ Quote sent
          </label>
          <select
            value={sourcing}
            onChange={(e) => setSourcing(e.target.value as SourcingFilter)}
            className="px-2 py-1 text-xs bg-background border border-border rounded"
            title="Filter by where the RFQ has been posted"
          >
            <option value="">Any sourcing</option>
            <option value="unposted">Not yet posted</option>
            {SOURCING_CHANNEL_VALUES.map((c) => (
              <option key={c} value={c}>Posted to {SOURCING_CHANNELS[c].label}</option>
            ))}
          </select>
        </div>
      </div>

      <div ref={scrollRef} className="flex-1 overflow-auto">
        {isLoading && <div className="p-6 text-sm text-muted-foreground">Loading…</div>}
        {isError && (
          <div className="p-6 text-sm text-destructive">
            {(error as { message?: string })?.message ?? 'Failed to load RFQs'}
          </div>
        )}
        {!isLoading && rfqs.length === 0 && (
          <div className="p-6 text-sm text-muted-foreground">
            {q || stage || aogOnly || urgentOnly || flaggedOnly || quoteSentOnly || sourcing ? 'No RFQs match your filters.' : 'No RFQs yet. Click "+ New" to parse one.'}
          </div>
        )}
        <ul>
          {rfqs.map((rfq) => (
            <li key={rfq.id}>
              <RfqRow
                rfq={rfq}
                isSelected={rfq.id === selectedRfqId}
                flagToggle={
                  <FlagToggle
                    isFlagged={rfq.isFlagged}
                    // One mutation serves every row, so `variables` scopes both
                    // states to the row that actually triggered it. KNOWN LIMIT:
                    // `variables` holds only the MOST RECENT toggle, so clicking
                    // two rows in quick succession clears the first row's
                    // spinner early. Both still reconcile on settle; per-row
                    // mutation instances would fix it and cost more than the
                    // symptom is worth.
                    isPending={toggleFlag.isPending && toggleFlag.variables?.id === rfq.id}
                    isError={toggleFlag.isError && toggleFlag.variables?.id === rfq.id}
                    onToggle={() => toggleFlag.mutate({ id: rfq.id, isFlagged: !rfq.isFlagged })}
                  />
                }
              />
            </li>
          ))}
        </ul>
        {/* Infinite-scroll sentinel + status */}
        <div ref={sentinelRef} />
        {isFetchingNextPage && (
          <div className="p-3 text-center text-xs text-muted-foreground">Loading more…</div>
        )}
        {!hasNextPage && rfqs.length > 0 && (
          <div className="p-3 text-center text-xs text-muted-foreground">
            {rfqs.length} RFQ{rfqs.length === 1 ? '' : 's'} · end of history
          </div>
        )}
      </div>
      </>
      )}
    </div>
  );
}
